import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
  TextField,
  InputAdornment,
  InputLabel,
  FormControl,
} from "@mui/material";
import { ArrowBack, Search } from "@mui/icons-material";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSystemHealthOverview } from "../../redux/actions";
import dayjs from "dayjs";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export default function ApiRequestLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [durationFilter, setDurationFilter] = useState("Last 24 Hours");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    dispatch(getSystemHealthOverview());
  }, [dispatch]);

  const apiRequestList =
    useSelector(
      (state) =>
        state.dashboard.getSystemHealthDashboard.data?.details?.api_requests
          ?.data
    ) || [];

  const [sortDirection, setSortDirection] = useState("asc");

  let filteredData = apiRequestList.filter((row) => {
    const rowTime = dayjs(row.TimeStamp, "DD/MM/YYYY, hh:mm A");
    const diffInHours = dayjs().diff(rowTime, "hour");

    if (durationFilter === "Last 1 Hour") return diffInHours < 1;
    if (durationFilter === "Last 6 Hours") return diffInHours < 6;
    if (durationFilter === "Last 12 Hours") return diffInHours < 12;
    return true;
  });

  if (searchQuery.trim() !== "") {
    filteredData = filteredData.filter((row) =>
      row.Endpoint.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  filteredData = filteredData.sort((a, b) => {
    const dateA = dayjs(a.TimeStamp, "DD/MM/YYYY, hh:mm A");
    const dateB = dayjs(b.TimeStamp, "DD/MM/YYYY, hh:mm A");

    if (sortDirection === "asc") {
      return dateA.isBefore(dateB) ? -1 : 1;
    } else {
      return dateA.isAfter(dateB) ? -1 : 1;
    }
  });

  return (
    <DashboardLayout>
      <Container maxWidth={false} sx={{ py: 4, px: 5 }}>
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Box display="flex" gap={2}>
            <FormControl
              size="small"
              sx={{
                width: "220px",
                backgroundColor: "#fff",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#BDBDBD",
                  },
                  "&:hover fieldset": {
                    borderColor: "#7B2D43",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#BDBDBD",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#757575",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#757575",
                },
              }}
            >
              <InputLabel id="duration-label">Duration</InputLabel>
              <Select
                labelId="duration-label"
                label="Duration"
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
              >
                <MenuItem value="Last 1 Hour">Last 1 Hour</MenuItem>
                <MenuItem value="Last 6 Hours">Last 6 Hours</MenuItem>
                <MenuItem value="Last 12 Hours">Last 12 Hours</MenuItem>
                <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
              </Select>
            </FormControl>

            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "4px",
                width: "240px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              borderColor: "#7B2D43",
              color: "#7B2D43",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#7B2D43",
                backgroundColor: "transparent",
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        </Box>

        <Typography
          variant="h6"
          fontWeight={600}
          mb={2}
          sx={{ textAlign: "left" }}
        >
          API Request Logs
        </Typography>

        <Box display="flex" justifyContent="center">
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #ddd",
              width: "100%",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#D0C5CA" }}>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Time stamp
                      </Typography>
                      <Box display="flex" flexDirection="column" sx={{ ml: 1 }}>
                        <Box
                          onClick={() => setSortDirection("asc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            mb: "2px",
                          }}
                        >
                          <ArrowDropUpIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "asc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={() => setSortDirection("desc")}
                          sx={{
                            background: "rgb(225, 225, 225)",
                            border: "1px solid #ccc",
                            borderRadius: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        >
                          <ArrowDropDownIcon
                            sx={{
                              fontSize: 16,
                              color:
                                sortDirection === "desc" ? "#7B2D43" : "#999",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Endpoint
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Method
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Status Code
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 3, py: 2 }}>
                    Response Time(ms)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        "& td": {
                          py: 2.5,
                          borderBottom: "1px solid #ddd",
                        },
                      }}
                    >
                      <TableCell>{row.TimeStamp}</TableCell>
                      <TableCell>{row.Endpoint}</TableCell>
                      <TableCell>{row.Method}</TableCell>
                      <TableCell>{row.StatusCode}</TableCell>
                      <TableCell>{row.ResponseTime}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Container>
    </DashboardLayout>
  );
}
